import { Masonry } from '@leagueplatform/masonry';
import { HeadingText, StackLayout } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { MASONRY } from 'common/constants';
import { hiddenMasonryNodeStyles } from 'common/plan-switcher-masonry-styles';
import { OnboardingCard } from 'components/onboarding/onboarding-card/onboarding-card.component';
import { useIsOnboardingMobile } from 'hooks/use-is-onboarding-mobile/use-is-onboarding-mobile.hook';
import { usePlanSwitcherGateCompletion } from 'hooks/use-plan-switcher-gate-completion/use-plan-switcher-gate-completion.hook';
import { usePlanSwitcherGateTitle } from 'hooks/use-plan-switcher-gate-title/use-plan-switcher-gate-title.hook';

type PlanSwitcherGateProps = {
  // Runs when the gate completes — when the server-driven node emits its
  // "/member/home" deeplink (single-plan auto-select or multi-plan submit).
  // Scenario decides the effect: returning login routes Home, the onboarding
  // step advances to the next step.
  onComplete: () => void;
};

// Full-viewport, non-dismissible surface for the Plan Switcher gate
// (`planSwitcher$root`). It renders no AppNavBar/footer and is not wrapped in a
// route — the member cannot navigate away until the gate completes. The shell
// itself is the shared OnboardingCard, so the gate stays visually identical to
// the sibling onboarding screens (and switches to its full-bleed mobile layout
// at the same breakpoint they do). The node is server-driven: exactly one child
// renders per member — the single-plan
// gate's loader (auto-selects then deeplinks Home), the multi-plan picker, or an
// error — and the client reacts to the "/member/home" completion via
// usePlanSwitcherGateCompletion.
//
// The "Your plans" card (white, elevated, title inside per design) is revealed
// ONLY once the picker's server-driven `setTitle` fires — see
// usePlanSwitcherGateTitle. Loading and the single-plan auto-resolve emit no
// title, so they render as a bare centered spinner rather than an empty boxed
// card; that is why the gate no longer flashes a card at members who are
// resolved straight past it. Shared by the returning-login gate and the
// first-login onboarding step so the surface lives in one place.
export const PlanSwitcherGate = ({ onComplete }: PlanSwitcherGateProps) => {
  const { formatMessage } = useIntl();
  usePlanSwitcherGateCompletion(onComplete);
  usePageViewAnalytics({ screen_name: 'Your Plans' });
  useDocumentTitle(formatMessage({ id: 'STR_YOUR_PLANS' }));
  const title = usePlanSwitcherGateTitle();
  const isOnboardingMobile = useIsOnboardingMobile();

  return (
    <OnboardingCard
      // No card until the picker's server-driven title arrives: loading and
      // the single-plan auto-resolve emit no title, so they render as a bare
      // centered spinner rather than an empty boxed card.
      hasCardChrome={Boolean(title)}
      surfaceCss={{
        // On mobile the content flows from the top rather than sitting
        // centered, matching the full-bleed mobile frame.
        alignItems: isOnboardingMobile ? 'flex-start' : 'center',
        // The single-plan gate is a server-driven `loader` node that web
        // renders as a skeleton bar — a jarring flash on the bare gate while
        // its auto-select dataprocessor runs (the root-fetch containerSpinner
        // has already unmounted by then). Hide it: members briefly see the
        // bare surface before the deeplink routes Home. This also keeps the
        // persist-failure reveal clean — the SDK loader renderer drops
        // engineClassName, so the server's hide-loader action can't reach
        // this element on web and the skeleton would otherwise linger next
        // to the revealed selectionError.
        '& [data-testid$="singlePlanGate"]': { display: 'none' },
      }}
    >
      <StackLayout
        // $half, not $oneAndHalf: the only gap this spacing owns is
        // title→subtitle (the subtitle is the Masonry surface's first
        // element), and per design they read as one tight header cluster.
        spacing="$half"
        horizontalAlignment="stretch"
        css={
          title
            ? {
                paddingBlockStart: '$twoAndHalf',
                // Per design the footer is a snug bar: ~$one between the
                // button and the card's bottom edge, unlike the $twoAndHalf
                // padding everywhere else (Figma 6120-195652).
                paddingBlockEnd: '$one',
                paddingInline: isOnboardingMobile
                  ? '$oneAndHalf'
                  : '$twoAndHalf',
                // Gate-only, per design: the footer divider (the services
                // `footerDivider` node, an <hr> with data-testid
                // `divider$…footerDivider`) bleeds edge-to-edge across the
                // card. Host-side selector — the divider renderer-override
                // route was dropped in 30acff1 after failing to render.
                // Bleed MUST equal this stack's paddingInline; explicit rem
                // ($twoAndHalf = 40px = 4rem, $oneAndHalf = 24px = 2.4rem
                // at Genesis's 10px/rem) since Stitches can't negate tokens
                // inside calc(). Width widens too: the Divider <hr> sets an
                // explicit width, so negative margins alone only shift it.
                '& hr[data-testid$="footerDivider"]': {
                  marginInline: isOnboardingMobile ? '-2.4rem' : '-4rem',
                  // Genesis's horizontal Divider keeps the <hr> UA
                  // margin-block (~8px); the trailing half would stack on
                  // the form gap and push the button 24px below the divider
                  // vs 16px above the card edge. Zero it so the space above
                  // and below the button is even ($one each).
                  marginBlockEnd: '$none',
                  width: isOnboardingMobile
                    ? 'calc(100% + 4.8rem)'
                    : 'calc(100% + 8rem)',
                },
                // Gate-only, per design: the server-driven subtitle (the
                // Masonry `header` contentBlock) centers on desktop and reads
                // left on mobile. Viewport-dependent, so it can't be server-
                // driven (a contentBlock has one `bodyTextAlignment`, used
                // as-is on native). Host-side selector like the divider
                // above; the two suffixes cover both locatorId forms services
                // can emit (`contentBlock$header` / `…$planSwitcherHeader`)
                // without catching the error states' contentBlocks. `& p`
                // because the default renderer sets textAlign on its
                // paragraph.
                '& [data-testid$="header"] p, & [data-testid$="Header"] p': {
                  textAlign: isOnboardingMobile ? 'start' : 'center',
                },
                // Collapse the phantom stack gaps left by hidden Masonry
                // nodes (selectionError reveal target + plan-count field).
                // Shared with PlanSwitcherChrome so they can't drift.
                ...hiddenMasonryNodeStyles,
              }
            : undefined
        }
      >
        {/* Stable keys so the Masonry surface stays mounted (no refetch) when
              the title arrives and the card appears around it. The subtitle is
              server-driven (the Masonry `header` node, below this title) — its
              desktop/mobile alignment is the host rule above. */}
        {title ? (
          <HeadingText
            key="plan-switcher-title"
            size="lg"
            level="1"
            css={{ textAlign: 'center' }}
          >
            {title}
          </HeadingText>
        ) : null}
        <Masonry
          key="plan-switcher-masonry"
          rootNodeId={MASONRY.ROOT_NODE_ID_PLAN_SWITCHER}
          // SDK reload treats undefined → {} as changed, but its query key
          // does not. Starting with {} makes the first retry refetch.
          initialQueryParams={{}}
        />
      </StackLayout>
    </OnboardingCard>
  );
};
